import { useState, useEffect } from 'react';
import { Eye, EyeOff, FingerprintPattern as Fingerprint, KeyRound } from 'lucide-react';
import MatrixRain from '@/components/MatrixRain';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { validatePassword } from '@/lib/passwordValidation';
import { setRememberDeviceFlag } from '@/lib/trustedDevices';
import { isWebAuthnSupported, authenticateWithBiometric, hasCredentialForEmail, getWebAuthnErrorMessage } from '@/lib/webauthn';

export default function AuthPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [webauthnSupported] = useState(() => isWebAuthnSupported());
  const { refreshMfaStatus } = useAuth();

  useEffect(() => {
    if (!isWebAuthnSupported()) return;
    if (!email || !email.includes('@')) {
      setBiometricAvailable(false);
      return;
    }
    const timer = setTimeout(() => {
      hasCredentialForEmail(email).then(setBiometricAvailable).catch(() => setBiometricAvailable(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [email]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordErrors([]);

    const check = validatePassword(password);
    if (!check.valid) {
      setPasswordErrors(check.errors);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError(authError.message);
        return;
      }

      if (data.user) {
        setRememberDeviceFlag(rememberDevice);
        await refreshMfaStatus();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  }

  async function handleBiometricLogin() {
    setBiometricLoading(true);
    setError('');
    try {
      const session = await authenticateWithBiometric(email);
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });
      if (sessionError) throw sessionError;
      setRememberDeviceFlag(rememberDevice);
      await refreshMfaStatus();
    } catch (err: unknown) {
      setError(getWebAuthnErrorMessage(err));
    } finally {
      setBiometricLoading(false);
    }
  }

  async function handlePasskeyLogin() {
    setPasskeyLoading(true);
    setError('');
    try {
      const session = await authenticateWithBiometric();
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });
      if (sessionError) throw sessionError;
      setRememberDeviceFlag(rememberDevice);
      await refreshMfaStatus();
    } catch (err: unknown) {
      setError(getWebAuthnErrorMessage(err));
    } finally {
      setPasskeyLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-[55%] bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 z-[1]">
          <MatrixRain />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center bg-white/[0.08] backdrop-blur-xl border-2 border-white/60 shadow-[0_6px_30px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-1px_0_rgba(0,0,0,0.1)]">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
              <img
                src="/assets/images/Logo_MB.png"
                alt="X Manage"
                className="w-10 h-10 object-contain relative z-10"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">X MANAGE</h1>
              <p className="text-xs text-white/60">by Mysterious Brothers</p>
            </div>
          </div>

          <div className="max-w-lg">
            <h2 className="text-5xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
              Business<br />Management,<br />
              <span className="text-teal-400">Simplified.</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-10">
              Track payments, manage exchange rates, monitor expenses,
              and maximize your revenue -- all from one powerful dashboard.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {[
                { title: 'Stripe Sync', desc: 'Auto-import payments' },
                { title: 'Exchange Rates', desc: 'Track MVR conversions' },
                { title: 'Card Limits', desc: 'Monitor customer cards' },
                { title: 'Analytics', desc: 'Revenue insights' },
              ].map((f) => (
                <div key={f.title} className="bg-white/5 border border-white/10 rounded-xl p-4 glass-hover">
                  <h3 className="font-semibold text-white text-sm mb-0.5">{f.title}</h3>
                  <p className="text-xs text-white/60">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs text-white/50">Mysterious Brothers (Pvt) Ltd</p>
            <p className="text-xs text-white/50">Developed by Mohamed Zaheen, Ibrahim Ziyam and Mohamed Razaan Jaufar</p>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[45%] flex items-center justify-center p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900" />
        <div className="absolute inset-0 z-[1]">
          <MatrixRain />
        </div>
        <div className="relative z-10 w-full max-w-[420px] liquid-glass p-8">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center bg-white/[0.08] backdrop-blur-xl border-2 border-white/60 shadow-[0_6px_30px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-1px_0_rgba(0,0,0,0.1)]">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
              <img
                src="/assets/images/Logo_MB.png"
                alt="X Manage"
                className="w-10 h-10 object-contain relative z-10"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
            <span className="text-xl font-bold text-white">X Manage</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-1 tracking-tight">Welcome back</h2>
            <p className="text-white/60 text-sm">Sign in to continue to your dashboard</p>
          </div>

          {error && (
            <div className="mb-5 p-3 bg-red-500/20 border border-red-400/30 rounded-xl">
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          {passwordErrors.length > 0 && (
            <div className="mb-5 p-3 bg-amber-500/20 border border-amber-400/30 rounded-xl">
              <p className="text-sm font-medium text-amber-200 mb-1">Password requirements:</p>
              <ul className="text-xs text-amber-200/80 space-y-0.5">
                {passwordErrors.map((err) => (
                  <li key={err}>- {err}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-1.5">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-sm text-white focus:ring-2 focus:ring-teal-400/30 focus:border-teal-400/50 outline-none transition-all duration-200 placeholder:text-white/40"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-sm text-white focus:ring-2 focus:ring-teal-400/30 focus:border-teal-400/50 outline-none transition-all duration-200 pr-11 placeholder:text-white/40"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-white/10 text-teal-500 focus:ring-teal-400/30 focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-xs text-white/60">Remember this device for 30 days</span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full glass-button-active text-white font-semibold py-2.5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <img
                    src="/assets/images/Logo_MB.png"
                    alt=""
                    className="w-4 h-4 object-contain"
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                </>
              )}
            </button>
          </form>

          {webauthnSupported && (
            <>
              <div className="flex items-center gap-3 mt-5">
                <div className="flex-1 h-px bg-white/15"></div>
                <span className="text-[11px] text-white/50 uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-white/15"></div>
              </div>
              <button
                type="button"
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading || loading || biometricLoading}
                className="mt-4 w-full glass-button text-white font-semibold py-2.5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                {passkeyLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <KeyRound size={16} />
                    Sign in with Passkey
                  </>
                )}
              </button>
            </>
          )}

          {biometricAvailable && (
            <button
              type="button"
              onClick={handleBiometricLogin}
              disabled={biometricLoading || loading || passkeyLoading}
              className="mt-3 w-full glass-button text-white font-semibold py-2.5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              {biometricLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Fingerprint size={16} />
                  Sign in with Fingerprint / Face ID
                </>
              )}
            </button>
          )}

          <p className="mt-8 text-center text-xs text-white/50">
            Mysterious Brothers (Pvt) Ltd
          </p>
        </div>
      </div>
    </div>
  );
}
